import React, { useState, useEffect } from 'react';
import {
    DataTable,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableHeader,
    TableBody,
    TableCell,
    Toggle,
    TableToolbar,
    TableToolbarContent,
    Button,
    InlineNotification
} from '@carbon/react';
import { useNavigate } from "react-router-dom";
import { Edit, Map, Link as LinkIcon, Image, TrashCan, CheckmarkOutline, MisuseOutline, ArrowRight } from '@carbon/react/icons';
import style from './AdvertisementsList.module.scss'
import endpoints from '../constants/endpoints.constants'
import { adTypes } from '../constants/form.constants'
import { genericRequest } from "../utils/request";
import Loader from "./Loading";

// Landing page style for toolbar buttons
const toolbarButtonStyle = {
    padding: '17px',
    width: '200px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px'
};

const iconStyle = {
    marginLeft: '10px',
    fill: 'white'
};

const AdvertisementsList = (props) => {
    const navigate = useNavigate();
    const [showDeleteSupportMessage, setShowDeleteSupportMessage] = useState(false);
    const [requestStatusEdit, setRequestStatusEdit] = useState([]);
    const [requestStatusDelete, setRequestStatusDelete] = useState({
        isError: false,
        isSuccess: false,
        isLoading: false,
    });

    const toggleApproval = async ({ approvalStatus, approvalListId, advertId, event }) => {
        requestStatusEdit[approvalListId] = { isLoading: true }
        setRequestStatusEdit(previousState => {
            previousState[approvalListId] = { isLoading: true }
            return [...previousState]
        })

        const request = await genericRequest({
            endpoint: 'toggleAdvertisementApproval',
            method: "POST",
            data: {
                advertisementId: advertId,
                approvalStatus,
            },
        });

        if (request.error) {
            console.error('Failed to toggle approval', request.error);
        }

        return setRequestStatusEdit(previousState => {
            previousState[approvalListId] = { isLoading: false }
            return [...previousState]
        })
    };

    const deleteAdvertisement = async ({ advertisement }) => {
        if (!window.confirm("Do you really want to delete?")) {
            return false
        }

        setRequestStatusDelete({ isLoading: true });

        const request = await genericRequest({
            endpoint: 'deleteAdvertisement',
            method: "POST",
            data: { advertisementId: advertisement.id },
        });

        if (request.error) {
            return setRequestStatusDelete({ isError: true, isLoading: false });
        }

        props.advertisementList.splice(props.advertisementList.findIndex(advert => advert.id === advertisement.id), 1)
        setRequestStatusDelete({ isSuccess: true, isLoading: false });
        return setShowDeleteSupportMessage(advertisement)
    };

    useEffect(() => {
        const requestStatusBuilder = [];
        props.advertisementList.forEach(() => {
            requestStatusBuilder.push({ isLoading: false })
        })

        setShowDeleteSupportMessage(false)
        setRequestStatusEdit(requestStatusBuilder)
    }, [props.advertisementList, navigate])

    const headerData = [
        {
            header: "Name",
            key: "ad_name",
        },
        {
            header: "Company Logo",
            key: "company_logo",
        },
        {
            header: "Company Name",
            key: "company_name",
        },
        {
            header: "Location",
            key: "ad_location",
        },
        {
            header: "Link",
            key: "ad_link",
        },
        {
            header: "Type",
            key: "ad_type",
        },
        {
            header: "Stories List",
            key: "stories_list",
        },
        {
            header: "Paid",
            key: "paid",
        },
        {
            header: "Edit",
            key: "edit",
        },
        {
            header: "Delete",
            key: "deleteAd",
        },
    ];

    if (props.userData.isAdmin) {
        headerData.push({
            header: "Approved",
            key: "approval",
        })
    }

    return (
        <>
            <Loader isLoading={requestStatusDelete.isLoading}
                isError={requestStatusDelete.isError}
                isSuccess={requestStatusDelete.isSuccess}
                errorMessage={"We Couldn't Delete the Advertisement"}
                successMessage={"Deleted Advertisement"}
            />
            <DataTable isSortable
                headers={headerData}
                rows={
                    props.advertisementList.map((advert, key) => (
                        {
                            id: advert.id.toString(),
                            ad_name: advert.advertisementName,
                            ad_location: (
                                <a target="_blank"
                                    rel="noopener noreferrer"
                                    href={endpoints.googleMaps({
                                        latitude: advert.companyLatitude,
                                        longitude: advert.companyLongitude
                                    })}
                                >
                                    <Map />
                                </a>
                            ),
                            ad_link: (
                                <a target="_blank"
                                    rel="noopener noreferrer"
                                    href={advert.advertisementLink}
                                >
                                    <LinkIcon />
                                </a>
                            ),
                            ad_type: adTypes.find(type => type.databaseId === advert.advertisementTypeList) ?
                                adTypes.find(type => type.databaseId === advert.advertisementTypeList).text : "Advert",
                            company_name: advert.companyName,
                            company_logo: (
                                <a target="_blank"
                                    rel="noopener noreferrer"
                                    href={endpoints.mediaUpload({ objectId: advert.Medium.objectKey })}
                                >
                                    <Image />
                                </a>
                            ),
                            stories_list: (
                                <span>Listed on {advert.Stories.length} Stories</span>
                            ),
                            approval: (
                                <div>
                                    <Toggle
                                        size="sm"
                                        aria-label="Approval Toggle"
                                        defaultToggled={advert.isApproved}
                                        id={`approval-toggle-${advert.id}`}
                                        labelText={''}
                                        disabled={requestStatusEdit[key] ? requestStatusEdit[key].isLoading : false}
                                        onToggle={async (checked) => {
                                            await toggleApproval({
                                                advertId: advert.id,
                                                approvalListId: key,
                                                approvalStatus: checked,
                                            })
                                        }}
                                    />
                                </div>
                            ),
                            paid: (advert.isPaid ? <CheckmarkOutline /> : <MisuseOutline />),
                            edit: (
                                <button
                                    className={style.styleLessButton}
                                    rel="noopener noreferrer"
                                    onClick={() => navigate(endpoints.advertisementDetails({ advertisementId: advert.id }))}
                                >
                                    <Edit />
                                </button>
                            ),
                            deleteAd: (
                                <button
                                    className={style.styleLessButton}
                                    rel="noopener noreferrer"
                                    onClick={() => deleteAdvertisement({ advertisement: advert })}
                                >
                                    <TrashCan />
                                </button>
                            ),
                        })
                    )}
            >
                {({ rows, headers, getHeaderProps }) => (
                    <TableContainer
                        title="Advertisement List"
                        description={
                            <>
                                <span><br />Your Advertisements Will Be Shown Here</span>
                                {showDeleteSupportMessage && (<InlineNotification
                                    kind={'warning'}
                                    subtitle={`Please contact support@yesterdaystories.com.au to cancel your subscription. Your Subscription Id is ${showDeleteSupportMessage.stripeSubscriptionId}`}
                                    title="Important"
                                    hideCloseButton={true}
                                />)}
                            </>
                        }>
                        <TableToolbar>
                            <TableToolbarContent>
                                <Button
                                    tabIndex={1}
                                    onClick={() => navigate(endpoints.createAdvertisement())}
                                    kind="primary"
                                    style={toolbarButtonStyle}>
                                    Add New Advertisement <ArrowRight style={iconStyle} />
                                </Button>
                            </TableToolbarContent>
                        </TableToolbar>
                        <Table useZebraStyles size='md'>
                            <TableHead>
                                <TableRow>
                                    {headers.map(header => {
                                        const { key, ...headerProps } = getHeaderProps({ header });
                                        return (
                                            <TableHeader key={key} {...headerProps}>
                                                {header.header}
                                            </TableHeader>
                                        );
                                    })}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map(row => (
                                    <TableRow key={row.id}>
                                        {row.cells.map(cell => (
                                            <TableCell className={
                                                cell.id.includes("ad_location") ||
                                                    cell.id.includes("ad_link") ||
                                                    cell.id.includes("company_logo") ||
                                                    cell.id.includes("approval") ||
                                                    cell.id.includes("paid") ||
                                                    cell.id.includes("deleteAd") ||
                                                    cell.id.includes("edit")
                                                    ? style.centreAlign : ''
                                            } key={cell.id}>{cell.value}</TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DataTable>
        </>
    );
};

export default AdvertisementsList;
